<template>
  <v-btn v-if="canMerge" class="ml-2" color="purple" :disabled="selectedIds.length < 2 || selectedIds.length > 50"
    @click="openMerge">Merge selected</v-btn>
  <v-dialog v-model="visible" max-width="1100" :persistent="applying">
    <v-card>
      <v-card-title>Merge operations</v-card-title>
      <v-card-text>
        <p class="mb-3">Choose the operation to keep. Preview the combined amounts and documents before confirming.</p>
        <v-select v-model="targetId" :items="choices" item-title="label" item-value="id"
          label="Operation to keep" :disabled="applying" @update:model-value="invalidatePreview" />
        <v-alert v-if="error" type="error" variant="tonal" class="mb-3">{{ error }}</v-alert>
        <v-progress-linear v-if="previewing" indeterminate />
        <template v-if="preview">
          <h3 class="mb-2">Before merge</h3>
          <v-table density="compact">
            <thead><tr><th>Action / ID</th><th>Ticket / passenger</th><th>Customer</th><th>Sale</th><th>Cost</th></tr></thead>
            <tbody><tr v-for="operation in preview.operations" :key="operation.id">
              <td>{{ operation.id === preview.target ? 'Keep' : 'Delete' }}<div class="text-caption">{{ operation.id }}</div></td>
              <td>{{ operation.ticket || '—' }}<div>{{ operation.passengers || '—' }}</div></td>
              <td>{{ operation.customer }}</td><td>{{ money(operation.sale, operation.currency) }}</td>
              <td>{{ money(operation.cost, operation.currency) }}</td>
            </tr></tbody>
          </v-table>
          <h3 class="mt-4">After merge</h3>
          <p>Sale: <strong>{{ money(preview.result.sale_price, currency) }}</strong> ·
            Cost: <strong>{{ money(preview.result.cost_total, currency) }}</strong></p>
          <p v-if="preview.result.sale_iva != null || preview.result.cost_iva != null">
            Sale IVA: {{ money(preview.result.sale_iva, currency) }} · Cost IVA: {{ money(preview.result.cost_iva, currency) }}</p>
          <p>Tickets: {{ preview.result.ticket_number || '—' }}</p>
          <p class="preserve-lines">Passengers: {{ preview.result.passengers || '—' }}</p>
          <p>Retained date: {{ date(preview.retainedFields.date) }} · Platform: {{ preview.retainedFields.platform || '—' }} ·
            Op: {{ preview.retainedFields.op || '—' }} · Agent: {{ preview.retainedFields.agent || '—' }}</p>
          <p class="text-caption">Other fields belonging only to the target are retained.</p>
          <details class="mt-2"><summary>Combined description</summary><p class="preserve-lines">{{ preview.result.description }}</p></details>
          <h4 class="mt-4">Payment allocations</h4>
          <p class="text-caption">Each original allocation stays separate, including entries with the same Payment ID.</p>
          <v-table density="compact"><thead><tr><th>Direction</th><th>Payment ID</th><th>Amount</th></tr></thead>
            <tbody><tr v-for="(payment, index) in allocations" :key="payment.direction + ':' + (payment._id || index)">
              <td>{{ payment.direction }}</td><td>{{ payment.payment }}</td><td>{{ money(payment.amount, currency) }}</td>
            </tr><tr v-if="!allocations.length"><td colspan="3">No payment allocations</td></tr></tbody>
          </v-table>
          <h4 class="mt-4">Provider documents</h4>
          <p>{{ preview.providerLinksToMove }} links will move to the retained operation.</p>
          <v-table v-if="preview.providerDocuments.length" density="compact">
            <thead><tr><th>UUID</th><th>Status</th><th>Current operation</th></tr></thead>
            <tbody><tr v-for="document in preview.providerDocuments" :key="document.id">
              <td>{{ document.uuid }}</td><td>{{ document.status }}</td><td>{{ document.operation }}</td>
            </tr></tbody>
          </v-table>
          <p v-if="preview.legacyProviderFieldRemoved" class="mt-2">The legacy provider-document field will be cleared; the active CFDI links are retained.</p>
          <v-alert type="warning" variant="tonal" class="mt-4">
            {{ preview.sourceOperationsToDelete }} source operations will be deleted. Original records are saved in merge history.
            There is no automatic undo.
          </v-alert>
          <v-checkbox v-model="confirmed" :disabled="applying" hide-details
            :label="`I confirm keeping ${preview.target} and deleting ${preview.sourceOperationsToDelete} source operations.`" />
        </template>
      </v-card-text>
      <v-card-actions>
        <v-btn :disabled="applying" @click="visible = false">Close</v-btn><v-spacer />
        <v-btn :loading="previewing" :disabled="applying || !targetId" @click="loadPreview">Preview merge</v-btn>
        <v-btn color="purple" :loading="applying" :disabled="!canApply" @click="applyMerge">Confirm merge</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="historyVisible" max-width="850">
    <v-card>
      <v-card-title>Merge history</v-card-title>
      <v-card-text>
        <p class="text-caption">Operation {{ historyOperationId }} · latest 20 merges</p>
        <v-progress-linear v-if="historyLoading" indeterminate />
        <v-alert v-if="historyError" type="error" variant="tonal">{{ historyError }}</v-alert>
        <p v-else-if="!historyLoading && !history.length">No merges recorded for this operation.</p>
        <v-list v-else>
          <v-list-item v-for="entry in history" :key="entry._id" class="mb-3">
            <div>{{ date(entry.appliedAt) }} · {{ entry.actor?.username || 'Command-line merge' }}</div>
            <div>Retained: {{ entry.target }}</div><div>Sources: {{ entry.sources.join(', ') }}</div>
            <div v-if="entry.after">Sale {{ money(entry.after.sale_price, entry.after.currency || 'MXN') }} ·
              Cost {{ money(entry.after.cost_total, entry.after.currency || 'MXN') }}</div>
            <div class="text-caption">Audit: {{ entry._id }}</div>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions><v-spacer /><v-btn @click="historyVisible = false">Close</v-btn></v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { getApiErrorMessage } from '../utils/api';

export default {
  name: 'OperationMergeDialog',
  props: { canMerge: Boolean, operations: { type: Array, required: true }, selectedIds: { type: Array, required: true } },
  emits: ['merged'],
  data: () => ({ visible: false, selected: [], targetId: null, preview: null, confirmed: false,
    previewing: false, applying: false, error: '', requestSequence: 0,
    historyVisible: false, historyLoading: false, historyError: '', history: [], historyOperationId: '', historySequence: 0 }),
  computed: {
    choices() { return this.selected.map(o => ({ id: o._id, label: `${o.ticket_number || o.reservation_code || 'Operation'} · ${o.passengers || ''} · ${o._id}` })); },
    currency() { return this.preview?.operations.find(o => o.id === this.preview.target)?.currency || 'MXN'; },
    allocations() {
      if (!this.preview) return [];
      return [['From customer', 'payments_from_customer'], ['To provider', 'payments_to_provider']]
        .flatMap(([direction, field]) => this.preview.result[field].map(p => ({ ...p, direction })));
    },
    canApply() { return this.canMerge && this.visible && this.confirmed && !this.previewing && !this.applying
      && Boolean(this.preview?.planHash) && this.preview.target === this.targetId; },
  },
  watch: {
    visible(value) { if (!value) this.invalidatePreview(); },
    selectedIds: { deep: true, handler() { if (this.visible && !this.applying) { this.invalidatePreview(); this.visible = false; } } },
    canMerge(value) { if (!value && !this.applying) this.visible = false; },
  },
  methods: {
    money(value, currency) {
      if (value == null) return '—';
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency;
    },
    date(value) { return value ? String(value).replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC') : '—'; },
    invalidatePreview() { this.requestSequence++; this.preview = null; this.confirmed = false; this.error = ''; this.previewing = false; },
    openMerge() {
      if (!this.canMerge || this.selectedIds.length < 2 || this.selectedIds.length > 50) return;
      this.selected = this.operations.filter(o => this.selectedIds.includes(o._id)).map(o => ({ ...o }));
      if (this.selected.length !== this.selectedIds.length) return;
      this.invalidatePreview(); this.targetId = this.selected[0]._id; this.visible = true;
    },
    request() { return { targetId: this.targetId, sourceIds: this.selected.map(o => o._id).filter(id => id !== this.targetId) }; },
    async loadPreview() {
      if (!this.canMerge || !this.visible || this.applying || !this.targetId) return;
      this.invalidatePreview(); const sequence = this.requestSequence;
      this.previewing = true;
      try {
        const { data } = await axios.post(process.env.VUE_APP_API_BASE_URL + '/operation/merge/preview', this.request());
        if (sequence !== this.requestSequence || !this.visible) return;
        if (data.alreadyApplied) { this.visible = false; this.$emit('merged', data); return; }
        this.preview = data;
      } catch (error) {
        if (sequence === this.requestSequence) this.error = getApiErrorMessage(error, 'Unable to preview merge.');
      } finally { if (sequence === this.requestSequence) this.previewing = false; }
    },
    async applyMerge() {
      if (!this.canApply) return;
      const payload = { ...this.request(), planHash: this.preview.planHash };
      this.applying = true; this.error = '';
      try {
        const { data } = await axios.post(process.env.VUE_APP_API_BASE_URL + '/operation/merge/apply', payload);
        this.visible = false; this.$emit('merged', data);
      } catch (error) {
        this.invalidatePreview();
        this.error = getApiErrorMessage(error, 'Merge response could not be confirmed.') + ' Preview again before retrying; it will detect an already completed merge.';
      } finally { this.applying = false; }
    },
    async openHistory(operationId) {
      if (!this.canMerge) return;
      const sequence = ++this.historySequence;
      this.historyOperationId = operationId; this.history = []; this.historyError = ''; this.historyLoading = true; this.historyVisible = true;
      try {
        const { data } = await axios.get(process.env.VUE_APP_API_BASE_URL + '/operation/merge/' + encodeURIComponent(operationId) + '/history');
        if (sequence === this.historySequence) this.history = data;
      } catch (error) { if (sequence === this.historySequence) this.historyError = getApiErrorMessage(error, 'Unable to load merge history.'); }
      finally { if (sequence === this.historySequence) this.historyLoading = false; }
    },
  },
};
</script>
<style scoped>
.preserve-lines { white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
